<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AppImage from '@uiKit/elements/Image.vue'
import locationPinIcon from '@assets/images/propertyDetail/location-pin.svg'
import closesSoonIcon from '@assets/images/propertyDetail/closes-soon.svg'
import specAreaIcon from '@assets/images/propertyDetail/spec-area.svg'
import specTypeIcon from '@assets/images/propertyDetail/spec-type.svg'
import specBedIcon from '@assets/images/propertyDetail/spec-bed.svg'
import specBathIcon from '@assets/images/propertyDetail/spec-bath.svg'
import type { HeaderSpec, StatusChip, StatusChipTone } from '../utils/propertyDetailUtils'

// Title block under the gallery (Figma 32590:61561): title + bookmark,
// location + status chip, spec chips, the one-line summary, then a rule.

interface Props {
  title: string
  location: string
  statusChip: StatusChip | null
  specs: HeaderSpec[]
  shortDescription: string
  isBookmarked: boolean
}

defineProps<Props>()

defineEmits<{ (e: 'bookmark'): void }>()

// Figma's spec-chip glyphs (crop_free / corporate_fare / bed / bathtub).
const SPEC_ICON: Record<HeaderSpec['icon'], string> = {
  expand: specAreaIcon,
  building: specTypeIcon,
  bed: specBedIcon,
  bath: specBathIcon,
}

// Label colour per chip tone — the chip's border / fill live in the styles.
const CHIP_TEXT_COLOR: Record<StatusChipTone, string> = {
  warning: 'var(--p-warning-600)',
  primary: 'var(--p-primary-600)',
  success: 'var(--p-emerald-700)',
  info: 'var(--p-blue-800)',
}
</script>

<template>
  <Box direction="col" gap="4" class="pd-header">
    <Box direction="col" gap="14px">
      <Box align="center" justify="between" gap="3">
        <Text type="title-lg" weight="bold" is="h1" class="pd-header__title">{{ title }}</Text>
        <Box
          is="button"
          type="button"
          align="center"
          justify="center"
          class="pd-header__bookmark"
          :aria-label="isBookmarked ? 'Remove from saved properties' : 'Save property'"
          :aria-pressed="isBookmarked"
          @click="$emit('bookmark')"
        >
          <PiIcon
            :name="isBookmarked ? 'bookmark-fill' : 'bookmark'"
            class="pd-header__bookmark-icon"
          />
        </Box>
      </Box>

      <Box align="center" justify="between" gap="3" class="pd-header__meta">
        <Box v-if="location" align="center" gap="6px" class="pd-header__location">
          <AppImage :src="locationPinIcon" alt="" aria-hidden="true" class="pd-header__icon-15" />
          <Text type="body" weight="medium" color="var(--p-sliceal-gray-600)" is="span">{{
            location
          }}</Text>
        </Box>
        <Box
          v-if="statusChip"
          align="center"
          gap="1"
          class="pd-header__chip"
          :class="`pd-header__chip--${statusChip.tone}`"
        >
          <AppImage
            v-if="statusChip.icon === 'flame'"
            :src="closesSoonIcon"
            alt=""
            aria-hidden="true"
            class="pd-header__chip-svg"
          />
          <PiIcon v-else :name="statusChip.icon" class="pd-header__chip-icon" />
          <Text type="body" weight="semibold" :color="CHIP_TEXT_COLOR[statusChip.tone]" is="span">
            {{ statusChip.label }}
          </Text>
        </Box>
      </Box>

      <Box v-if="specs.length" align="center" gap="6px" class="pd-header__specs">
        <Box
          v-for="spec in specs"
          :key="spec.icon"
          align="center"
          gap="6px"
          class="pd-header__spec"
        >
          <AppImage
            :src="SPEC_ICON[spec.icon]"
            alt=""
            aria-hidden="true"
            class="pd-header__icon-15"
          />
          <Text type="body" weight="semibold" color="var(--p-primary-800)" is="span">
            {{ spec.label }}
          </Text>
        </Box>
      </Box>

      <Text v-if="shortDescription" type="body" class="pd-header__summary">
        {{ shortDescription }}
      </Text>
    </Box>
    <Box class="pd-header__rule" />
  </Box>
</template>

<style scoped>
.pd-header__title {
  line-height: 30px;
  min-width: 0;
}

.pd-header__bookmark {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 100px;
  background: var(--p-surface-50);
  filter: drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.1)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
  cursor: pointer;
}

.pd-header__bookmark:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.pd-header__bookmark-icon {
  font-size: 16px;
  color: var(--p-text-muted-color);
}

.pd-header__meta {
  margin-top: -2px;
  flex-wrap: wrap;
}

.pd-header__location {
  min-width: 0;
}

.pd-header__icon-15 {
  display: block;
  flex-shrink: 0;
  width: 15px;
  height: 15px;
}

.pd-header__chip {
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px solid;
  border-radius: 12px;
  white-space: nowrap;
}

.pd-header__chip-icon {
  font-size: 13px;
}

.pd-header__chip-svg {
  display: block;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

/* Tone palettes straight from the three status frames. */
.pd-header__chip--warning {
  background: var(--p-yellow-50);
  border-color: var(--p-warning-600);
  color: var(--p-warning-600);
}

.pd-header__chip--primary {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
  color: var(--p-primary-600);
}

.pd-header__chip--success {
  background: var(--p-emerald-50);
  border-color: var(--p-emerald-600);
  color: var(--p-emerald-700);
}

.pd-header__chip--info {
  background: var(--p-blue-50);
  border-color: var(--p-blue-700);
  color: var(--p-blue-700);
}

.pd-header__specs {
  flex-wrap: wrap;
}

.pd-header__spec {
  height: 28px;
  padding: 8px 13px;
  border: 1px solid var(--p-sliceal-gray-200);
  border-radius: 34px;
  background: var(--p-surface-0);
  backdrop-filter: blur(6px);
}

.pd-header__summary {
  line-height: 1.7;
  letter-spacing: -0.14px;
}

.pd-header__rule {
  height: 1px;
  background: var(--p-sliceal-gray-200);
}
</style>
